import axios from 'axios';
import { ErrorDto } from 'services/dtos/errors-dto';
import { useErrorHandler } from 'tools/error-handler';
import {
    Announcement,
    AnnouncementReadStats,
    Announcements,
    AnnouncementWithSite,
    CreateAnnouncementInput,
    CreateAnnouncementResponse,
    AdminSite,
} from './announcements.service';

const BASE = () => process.env.REACT_APP_API_MOBILE_PASSCODES_BASE_URL;
const auth = (accessToken: string) => ({ headers: { Authorization: 'Bearer ' + accessToken } });

interface AnnouncementDto {
    id: string;
    title: string;
    body: string;
    image_url: string | null;
    published_at: string;
    read: boolean;
}

interface CreateAnnouncementResponseDto {
    id: string;
    site_id: string;
    title: string;
    published_at: string;
}

interface AnnouncementWithSiteDto extends AnnouncementDto {
    site_id: string;
    site_name: string;
}

export interface AdminSiteDto {
    site_id: string;
    site_name: string;
    timezone?: string;
}

export const toAdminSite = (dto: AdminSiteDto): AdminSite => ({
    siteId: dto.site_id,
    siteName: dto.site_name,
    timezone: dto.timezone ?? '',
});

const toAnnouncement = (dto: AnnouncementDto): Announcement => ({
    id: dto.id,
    title: dto.title,
    body: dto.body,
    imageUrl: dto.image_url,
    publishedAt: dto.published_at,
    read: dto.read,
});

const toAnnouncementWithSite = (dto: AnnouncementWithSiteDto): AnnouncementWithSite => ({
    ...toAnnouncement(dto),
    siteId: dto.site_id,
    siteName: dto.site_name,
});

export const useAPIAnnouncements = (): Announcements => {
    const errorHandler = useErrorHandler();

    const getSiteAnnouncements = async (accessToken: string, siteId: string): Promise<Announcement[]> => {
        try {
            const { data } = await axios.get<AnnouncementDto[]>(
                `${BASE()}/user/sites/${siteId}/announcements`,
                auth(accessToken),
            );
            return data.map(toAnnouncement);
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    const createAnnouncement = async (
        accessToken: string,
        input: CreateAnnouncementInput,
    ): Promise<CreateAnnouncementResponse> => {
        try {
            const { data } = await axios.post<CreateAnnouncementResponseDto>(
                `${BASE()}/user/announcements`,
                { site_id: input.siteId, title: input.title, body: input.body, silent: input.silent },
                auth(accessToken),
            );
            return { id: data.id, siteId: data.site_id, title: data.title, publishedAt: data.published_at };
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    const markAnnouncementRead = async (accessToken: string, id: string): Promise<void> => {
        try {
            await axios.post(`${BASE()}/user/announcements/${id}/read`, {}, auth(accessToken));
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    const getAnnouncementReads = async (accessToken: string, id: string): Promise<AnnouncementReadStats> => {
        try {
            const { data } = await axios.get<AnnouncementReadStats>(
                `${BASE()}/user/announcements/${id}/reads`,
                auth(accessToken),
            );
            return data;
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    const deleteAnnouncement = async (accessToken: string, id: string): Promise<void> => {
        try {
            await axios.delete(`${BASE()}/user/announcements/${id}`, auth(accessToken));
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    const getUserAnnouncements = async (accessToken: string): Promise<AnnouncementWithSite[]> => {
        try {
            const { data } = await axios.get<AnnouncementWithSiteDto[]>(
                `${BASE()}/user/announcements`,
                auth(accessToken),
            );
            return data.map(toAnnouncementWithSite);
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw error.response.data.msg;
        }
    };

    // Sin errorHandler centralizado a propósito: esta llamada alimenta una opción
    // OPCIONAL del chooser del "+" (si falla, el chooser sigue mostrando sus 2
    // opciones existentes) — no queremos que un fallo de red acá navegue a /500
    // o /invalid-session y tape el resto del chooser, que sí funciona.
    const getAdminSites = async (accessToken: string): Promise<AdminSite[]> => {
        const { data } = await axios.get<AdminSiteDto[]>(`${BASE()}/user/admin-sites`, auth(accessToken));
        return data.map(toAdminSite);
    };

    return {
        getSiteAnnouncements,
        createAnnouncement,
        markAnnouncementRead,
        getAnnouncementReads,
        deleteAnnouncement,
        getUserAnnouncements,
        getAdminSites,
    };
};
